import React, { useEffect, useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { useUpdateCustomer } from "@/hooks/use-customers";
import type { DBCustomer } from "@/lib/db-types";
import type { UserLookup } from "@/hooks/use-users";
import { NewCustomerDialog } from "./NewCustomerDialog";
import { PdfOfferUpload } from "./PdfOfferUpload";
import type { ExtractedBudget } from "@/lib/offer-types";
import { SrIcon } from "@/components/ui/SrIcon";

interface StepBasicInfoProps {
  customers: DBCustomer[] | undefined;
  users: UserLookup[] | undefined;
  customerId: string;
  onCustomerChange: (id: string) => void;
  projectName: string;
  onProjectNameChange: (name: string) => void;
  projectCode: string;
  projectCodeLoading: boolean;
  totalAmount: string;
  onTotalAmountChange: (amount: string) => void;
  deadline: string;
  onDeadlineChange: (date: string) => void;
  /** VIB-1297: valgfri forventet oppstartsdato (i motsetning til deadline, som er påkrevd). */
  estimatedStartDate: string;
  onEstimatedStartDateChange: (date: string) => void;
  isInternal: boolean;
  onIsInternalChange: (internal: boolean) => void;
  projectType: "fixed" | "hourly";
  onProjectTypeChange: (type: "fixed" | "hourly") => void;
  projectStatus: string;
  onProjectStatusChange: (status: string) => void;
  productionLeadId: string;
  onProductionLeadChange: (id: string) => void;
  pdfUploaded: boolean;
  budgetLinesCount: number;
  onPdfReset: () => void;
  aiProgress: number;
  aiLoading: boolean;
  onOfferSelected: (offer: ExtractedBudget) => void;
  stepErrors: string[];
  /**
   * VIB-694: ConvertOfferDialog setter denne for å skjule PDF-opplasteren,
   * fordi budsjettlinjene allerede er importert fra tilbudet.
   */
  hideOfferUpload?: boolean;
  /** VIB-feedback: skjul «Internt prosjekt»/«Time-jobb»-bryterne. Settes ved
   *  konvertering fra tilbud — slike prosjekter er alltid eksterne + fastpris. */
  hideTypeToggles?: boolean;
}

export function StepBasicInfo({
  customers,
  users,
  customerId,
  onCustomerChange,
  projectName,
  onProjectNameChange,
  projectCode,
  projectCodeLoading,
  totalAmount,
  onTotalAmountChange,
  deadline,
  onDeadlineChange,
  estimatedStartDate,
  onEstimatedStartDateChange,
  isInternal,
  onIsInternalChange,
  projectType,
  onProjectTypeChange,
  projectStatus,
  onProjectStatusChange,
  productionLeadId,
  onProductionLeadChange,
  pdfUploaded,
  budgetLinesCount,
  onPdfReset,
  aiProgress,
  aiLoading,
  onOfferSelected,
  stepErrors,
  hideOfferUpload = false,
  hideTypeToggles = false,
}: StepBasicInfoProps) {
  const selectedCustomer = useMemo(
    () => customers?.find(c => c.id === customerId) ?? null,
    [customers, customerId]
  );

  // Lokal redigering av kunde-felt: skriver tilbake til customers-tabellen
  // ved blur slik at endringer er reflektert overalt der kunden vises.
  const updateCustomer = useUpdateCustomer();
  const [contactPersonDraft, setContactPersonDraft] = useState("");
  const [contactEmailDraft, setContactEmailDraft] = useState("");
  useEffect(() => {
    setContactPersonDraft(selectedCustomer?.contact_person ?? "");
    setContactEmailDraft(selectedCustomer?.contact_email ?? "");
  }, [selectedCustomer?.id, selectedCustomer?.contact_person, selectedCustomer?.contact_email]);

  const persistCustomerField = (field: "contact_person" | "contact_email", draft: string) => {
    if (!selectedCustomer) return;
    const current =
      field === "contact_person"
        ? selectedCustomer.contact_person ?? ""
        : selectedCustomer.contact_email ?? "";
    if (draft === current) return;
    updateCustomer.mutate({ id: selectedCustomer.id, field, value: draft });
  };

  return (
    <div className="space-y-6">
      {/* VIB-666: Time-jobber har ikke tilbuds-PDF. */}
      {/* VIB-694: ConvertOfferDialog skjuler også PDF-opplasteren — budsjettet
          er allerede importert fra tilbudet. */}
      {/* Internprosjekt har heller ikke tilbud — det er internt arbeid. */}
      {projectType !== "hourly" && !isInternal && !hideOfferUpload && (
        <PdfOfferUpload
          onOfferSelected={onOfferSelected}
          pdfUploaded={pdfUploaded}
          budgetLinesCount={budgetLinesCount}
          onReset={onPdfReset}
          aiProgress={aiProgress}
          aiLoading={aiLoading}
        />
      )}

      <div className="space-y-6">
        <div>
          <Label>Prosjektstatus</Label>
          <Select value={projectStatus} onValueChange={onProjectStatusChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Aktivt</SelectItem>
              <SelectItem value="kommende">Kommende</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {!isInternal && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="np-customer">
                Kunde <span className="text-destructive">*</span>
              </Label>
              <div className="flex gap-2">
                <Select value={customerId} onValueChange={onCustomerChange}>
                  <SelectTrigger
                    id="np-customer"
                    className={cn(
                      "flex-1",
                      stepErrors.includes("Kunde er obligatorisk") && "border-destructive"
                    )}
                  >
                    <SelectValue placeholder="Velg kunde..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(customers ?? []).map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                        {c.country_code && c.country_code !== "NO" ? ` (${c.country_code})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <NewCustomerDialog onCustomerCreated={onCustomerChange} />
              </div>
            </div>
          </div>
        )}

        <div>
          <Label htmlFor="np-project-name">
            Prosjektnavn <span className="text-destructive">*</span>
          </Label>
          <Input
            id="np-project-name"
            autoFocus
            value={projectName}
            onChange={e => onProjectNameChange(e.target.value)}
            placeholder={isInternal ? "Beskrivelse av internt arbeid" : "Kundenavn - Prosjektbeskrivelse"}
            className={cn(
              stepErrors.includes("Prosjektnavn er obligatorisk") && "border-destructive"
            )}
          />
        </div>

        <div>
          <Label>Prosjektkode</Label>
          <div className="relative">
            <Input
              value={projectCode}
              readOnly
              disabled
              placeholder={projectCodeLoading ? "Henter neste kode…" : "—"}
              aria-label="Prosjektkode (tildeles automatisk)"
              className="bg-muted/50 cursor-not-allowed"
            />
            {projectCodeLoading && (
              <SrIcon name="loader2" className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground"  />
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Tildeles automatisk ved opprettelse. Ekte kode kan avvike hvis to opprettes samtidig.
          </p>
        </div>

        {!isInternal && projectType !== "hourly" && (
          <div>
            <Label htmlFor="np-total-amount">
              Totalbeløp eks. mva <span className="text-destructive">*</span>
            </Label>
            <Input
              id="np-total-amount"
              type="number"
              value={totalAmount}
              onChange={e => onTotalAmountChange(e.target.value)}
              placeholder="Fylles inn fra tilbud"
              className={cn(
                stepErrors.includes("Totalbeløp eks. mva er obligatorisk") &&
                  "border-destructive"
              )}
            />
          </div>
        )}

        {!isInternal && (
          <div>
            <Label htmlFor="np-deadline">
              Estimert deadline <span className="text-destructive">*</span>
            </Label>
            <Input
              id="np-deadline"
              type="date"
              value={deadline}
              onChange={e => onDeadlineChange(e.target.value)}
              className={cn(
                stepErrors.includes("Estimert deadline er obligatorisk") && "border-destructive"
              )}
            />
          </div>
        )}

        {/* VIB-1297: valgfri forventet oppstartsdato — ingen validering, i motsetning til deadline. */}
        <div>
          <Label>Estimert oppstart</Label>
          <Input
            type="date"
            value={estimatedStartDate}
            onChange={e => onEstimatedStartDateChange(e.target.value)}
          />
        </div>

        {!hideTypeToggles && (
          <div className="flex flex-col gap-3 pt-6">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={isInternal}
                onCheckedChange={v => onIsInternalChange(!!v)}
                id="internal"
              />
              <Label htmlFor="internal" className="cursor-pointer">
                Internt prosjekt
              </Label>
            </div>
            {!isInternal && (
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={projectType === "hourly"}
                  onCheckedChange={v => onProjectTypeChange(v ? "hourly" : "fixed")}
                  id="hourly"
                />
                <Label htmlFor="hourly" className="cursor-pointer">
                  Time-jobb (faktureres per medgått tid)
                </Label>
              </div>
            )}
            {!isInternal && projectType === "hourly" && (
              <p className="text-xs text-muted-foreground -mt-1 ml-6">
                Hopper over Tilbud-/budsjett-steget. Faktureres løpende basert
                på loggede timer.
              </p>
            )}
            {isInternal && (
              <p className="text-xs text-muted-foreground -mt-1 ml-6">
                Hopper over Fakturering og Tilbud/budsjett — internt arbeid uten
                ekstern fakturering.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="border-t pt-6">
        <h3 className="text-sm font-medium mb-4">Referanser og roller</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="np-production-lead">
              Prosjektleder <span className="text-destructive">*</span>
            </Label>
            <Select value={productionLeadId} onValueChange={onProductionLeadChange}>
              <SelectTrigger
                id="np-production-lead"
                className={cn(
                  stepErrors.includes("Prosjektleder er obligatorisk") &&
                    "border-destructive"
                )}
              >
                <SelectValue placeholder="Velg prosjektleder..." />
              </SelectTrigger>
              <SelectContent>
                {(users ?? []).map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {!isInternal && (
            <>
              <div>
                <Label>Kontaktperson (kunde)</Label>
                <Input
                  value={contactPersonDraft}
                  onChange={e => setContactPersonDraft(e.target.value)}
                  onBlur={() => persistCustomerField("contact_person", contactPersonDraft)}
                  disabled={!selectedCustomer}
                  placeholder={selectedCustomer ? "Navn på kontaktperson" : "Velg kunde først"}
                />
              </div>
              <div>
                <Label>E-post (kunde)</Label>
                <Input
                  type="email"
                  value={contactEmailDraft}
                  onChange={e => setContactEmailDraft(e.target.value)}
                  onBlur={() => persistCustomerField("contact_email", contactEmailDraft)}
                  disabled={!selectedCustomer}
                  placeholder={selectedCustomer ? "navn@kunde.no" : "Velg kunde først"}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
